/**
 * Kosten (E17) auf dem Gerät. Alles teilbar wie die Aufgaben: mit
 * `householdId` sieht es der Haushalt, ohne ist es privat. Lokal zuerst,
 * dann die Outbox — Kosten gehen auch offline im Laden.
 */

import { newId, recurringDueDates, recurringExpenseId } from '@kraftwerk/shared';
import type { ExpenseCategory, ExpenseSource, RecurringInterval } from '@kraftwerk/shared';
import { currentOwnerId } from '../auth.js';
import { db } from './local.js';
import type {
  KraftwerkDatabase,
  LocalExpense,
  LocalExpenseCategory,
  LocalRecurringExpense,
  LocalSavingsDeposit,
  LocalSavingsGoal,
} from './local.js';
import { stamp, writeRow } from './write.js';

const live = <T extends { deletedAt: string | null }>(rows: T[]) =>
  rows.filter((row) => !row.deletedAt);

// --- Ausgaben -----------------------------------------------------------------

export async function listExpenses(
  month: string,
  database: KraftwerkDatabase = db,
): Promise<LocalExpense[]> {
  return live(await database.expenses.where('date').startsWith(month).toArray()).sort(
    (a, b) => b.date.localeCompare(a.date) || b.updatedAt.localeCompare(a.updatedAt),
  );
}

export interface ExpenseDraft {
  amountCents: number;
  category: ExpenseCategory;
  categoryId: string | null;
  date: string;
  note: string | null;
  paidBy: string | null;
  householdId: string | null;
}

export async function saveExpense(
  draft: ExpenseDraft,
  existing: LocalExpense | null = null,
  source: ExpenseSource = 'manual',
  database: KraftwerkDatabase = db,
): Promise<LocalExpense> {
  return writeRow(
    'expenses',
    {
      ...existing,
      id: existing?.id ?? newId(),
      ...draft,
      note: draft.note?.trim() || null,
      source: existing?.source ?? source,
      recurringId: existing?.recurringId ?? null,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

export async function deleteExpense(
  expense: LocalExpense,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const now = stamp();
  await writeRow('expenses', { ...expense, updatedAt: now, deletedAt: now }, 'delete', database);
}

/** Nach „Einkauf abschließen“: Betrag als Lebensmittel-Ausgabe, bezahlt von mir. */
export async function recordShoppingExpense(
  amountCents: number,
  date: string,
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalExpense> {
  return saveExpense(
    {
      amountCents,
      category: 'food',
      categoryId: null,
      date,
      note: null,
      paidBy: currentOwnerId(),
      householdId,
    },
    null,
    'shopping',
    database,
  );
}

// --- Kategorien und Budgets -------------------------------------------------------

export async function listCategories(
  database: KraftwerkDatabase = db,
): Promise<LocalExpenseCategory[]> {
  return live(await database.expense_categories.toArray());
}

/** Budget für eine feste Kategorie setzen (oder mit `null` entfernen). */
export async function setBudget(
  key: ExpenseCategory,
  budgetCents: number | null,
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = (await listCategories(database)).find(
    (row) => row.key === key && row.householdId === householdId,
  );
  await writeRow(
    'expense_categories',
    {
      ...existing,
      id: existing?.id ?? newId(),
      householdId,
      key,
      name: null,
      budgetCents,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

// --- Wiederkehrend ---------------------------------------------------------------

export async function listRecurring(
  database: KraftwerkDatabase = db,
): Promise<LocalRecurringExpense[]> {
  return live(await database.recurring_expenses.toArray()).sort((a, b) =>
    a.title.localeCompare(b.title, 'de'),
  );
}

export interface RecurringDraft {
  title: string;
  amountCents: number;
  category: ExpenseCategory;
  interval: RecurringInterval;
  day: number;
  month: number | null;
  startDate: string;
  paidBy: string | null;
  householdId: string | null;
}

export async function saveRecurring(
  draft: RecurringDraft,
  existing: LocalRecurringExpense | null = null,
  database: KraftwerkDatabase = db,
): Promise<LocalRecurringExpense> {
  return writeRow(
    'recurring_expenses',
    {
      ...existing,
      id: existing?.id ?? newId(),
      ...draft,
      title: draft.title.trim(),
      categoryId: null,
      endDate: existing?.endDate ?? null,
      active: existing?.active ?? true,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

export async function deleteRecurring(
  rule: LocalRecurringExpense,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const now = stamp();
  await writeRow(
    'recurring_expenses',
    { ...rule, updatedAt: now, deletedAt: now },
    'delete',
    database,
  );
}

/**
 * Fällige Buchungen anlegen — bis heute, höchstens drei Monate zurück. Mit
 * fester Kennung je Fälligkeit (`recurringExpenseId`): Zwei Geräte buchen
 * dieselbe Zeile, nicht zwei. Was schon da ist (auch von Hand geändert oder
 * gelöscht), bleibt, wie es ist.
 */
export async function bookDueRecurring(
  today: string,
  database: KraftwerkDatabase = db,
): Promise<number> {
  const from = threeMonthsBack(today);
  let booked = 0;
  for (const rule of await listRecurring(database)) {
    for (const date of recurringDueDates(rule, from, today)) {
      const id = recurringExpenseId(rule.id, date);
      if (await database.expenses.get(id)) continue;
      await writeRow(
        'expenses',
        {
          id,
          householdId: rule.householdId,
          paidBy: rule.paidBy,
          amountCents: rule.amountCents,
          category: rule.category,
          categoryId: rule.categoryId,
          date,
          note: rule.title,
          source: 'recurring',
          recurringId: rule.id,
          updatedAt: stamp(),
          deletedAt: null,
        },
        'upsert',
        database,
      );
      booked += 1;
    }
  }
  return booked;
}

function threeMonthsBack(today: string): string {
  const [year, month] = today.split('-').map(Number) as [number, number];
  const index = year * 12 + (month - 1) - 3;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}-01`;
}

// --- Sparziele --------------------------------------------------------------------

export async function listGoals(database: KraftwerkDatabase = db): Promise<LocalSavingsGoal[]> {
  return live(await database.savings_goals.toArray()).sort((a, b) =>
    a.name.localeCompare(b.name, 'de'),
  );
}

export async function listDeposits(
  database: KraftwerkDatabase = db,
): Promise<LocalSavingsDeposit[]> {
  return live(await database.savings_deposits.toArray());
}

export async function saveGoal(
  draft: { name: string; targetCents: number; householdId: string | null },
  database: KraftwerkDatabase = db,
): Promise<LocalSavingsGoal> {
  return writeRow(
    'savings_goals',
    {
      id: newId(),
      ...draft,
      name: draft.name.trim(),
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

/** Einzahlung — je Einzahlung eine Zeile, damit sich zwei gleichzeitige nicht überschreiben. */
export async function addDeposit(
  goal: LocalSavingsGoal,
  amountCents: number,
  date: string,
  database: KraftwerkDatabase = db,
): Promise<LocalSavingsDeposit> {
  return writeRow(
    'savings_deposits',
    {
      id: newId(),
      householdId: goal.householdId,
      goalId: goal.id,
      amountCents,
      date,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}
